import { Button, Group, Select, Text } from "@mantine/core";
import React, { useMemo, useState } from "react";
import { useServerApi } from "../hooks/userServerApi";
import { useTranslation } from "react-i18next";
import { showNotification } from "@mantine/notifications";
import { IconCircleCheck, IconExclamationMark } from "@tabler/icons";
import moment from "moment";

const inferAssessmentYearFromDate = (date = new Date()) => {
  const m = moment(date);
  return m.month() >= 3 ? m.year() + 1 : m.year();
};

const ReportIr56m = () => {
  const [loadingXml, setLoadingXml] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [api] = useServerApi();
  const { t } = useTranslation();
  const currentYear = inferAssessmentYearFromDate();
  const yearOptions = useMemo(() => {
    const years = [];
    for (let year = currentYear + 1; year >= currentYear - 9; year -= 1) {
      years.push({
        value: String(year),
        label: `${year} (${year - 1}/04 – ${year}/03)`,
      });
    }
    return years;
  }, [currentYear]);
  const [assessmentYear, setAssessmentYear] = useState(String(currentYear));

  const getErrorMessage = (result) => {
    const isConfigMissing =
      result?.error?.code === "IR56M_NOT_CONFIGURED" ||
      result?.message === "ERR_IR56M_NOT_CONFIGURED";

    if (isConfigMissing) {
      return t("report.ir56m.configWarningMessage");
    }

    return (
      result?.error?.message ||
      result?.message ||
      result?.data?.message ||
      t("report.ir56m.error")
    );
  };

  const downloadXml = async () => {
    try {
      setLoadingXml(true);
      const result = await api.Payroll2.getIr56mXml(Number(assessmentYear));
      if (result?.success) {
        const excludedCount = result?.data?.excludedCount || 0;
        showNotification({
          title: t("report.ir56m.title"),
          color: excludedCount > 0 ? "yellow" : "green",
          icon: <IconCircleCheck size={18} />,
          message:
            excludedCount > 0
              ? t("report.ir56m.successWithExcluded", { count: excludedCount })
              : t("report.ir56m.success"),
          autoClose: excludedCount > 0 ? 10000 : 6000,
        });
        return;
      }

      showNotification({
        title: t("report.ir56m.title"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: getErrorMessage(result),
        autoClose: 8000,
      });
    } catch (error) {
      console.log(error);
      showNotification({
        title: t("report.ir56m.title"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: t("report.ir56m.error"),
      });
    } finally {
      setLoadingXml(false);
    }
  };

  const downloadExcel = async () => {
    try {
      setLoadingExcel(true);
      const result = await api.Payroll2.getIr56mExcel(Number(assessmentYear));
      if (result?.success) {
        const excludedCount = result?.data?.excludedCount || 0;
        showNotification({
          title: t("report.ir56m.excelTitle"),
          color: excludedCount > 0 ? "yellow" : "green",
          icon: <IconCircleCheck size={18} />,
          message:
            excludedCount > 0
              ? t("report.ir56m.excelSuccessWithExcluded", {
                  count: excludedCount,
                })
              : t("report.ir56m.excelSuccess"),
          autoClose: excludedCount > 0 ? 10000 : 6000,
        });
        return;
      }

      showNotification({
        title: t("report.ir56m.excelTitle"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: getErrorMessage(result) || t("report.ir56m.excelError"),
        autoClose: 8000,
      });
    } catch (error) {
      console.log(error);
      showNotification({
        title: t("report.ir56m.excelTitle"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: t("report.ir56m.excelError"),
      });
    } finally {
      setLoadingExcel(false);
    }
  };

  return (
    <div>
      <Text size="xs">{t("report.ir56m.description")}</Text>
      <Group mt="md" align="flex-end">
        <Select
          label={t("report.ir56m.assessmentYear")}
          description={t("report.ir56m.assessmentYearHint")}
          data={yearOptions}
          value={assessmentYear}
          onChange={(value) => setAssessmentYear(value || assessmentYear)}
          size="xs"
          style={{ width: 280 }}
          required
        />
        <Button size="xs" onClick={downloadXml} loading={loadingXml}>
          {t("report.ir56m.downloadXml")}
        </Button>
        <Button
          size="xs"
          color="green"
          onClick={downloadExcel}
          loading={loadingExcel}
        >
          {t("report.ir56m.downloadExcel")}
        </Button>
      </Group>
    </div>
  );
};

export default ReportIr56m;
